import type { ServiceSelection } from "../../shared/types.js";
import { CheckIcon, FlaskIcon, ClockIcon } from "../../components/Icons.js";

type Preset = "attest" | "epc" | "elektrisch" | "combo";

function presetFor(service: ServiceSelection): Preset {
  const hasEpc = service.addOns.includes("epc");
  const hasElectric = service.addOns.includes("elektrische_keuring");
  if (hasEpc && hasElectric) return "combo";
  if (hasEpc) return "epc";
  if (hasElectric) return "elektrisch";
  return "attest";
}

export function ServiceSelector({
  service,
  onChange,
}: {
  service: ServiceSelection;
  onChange: (s: ServiceSelection) => void;
}) {
  const current = presetFor(service);

  function selectPreset(preset: Preset) {
    const addOns: ServiceSelection["addOns"] =
      preset === "combo" ? ["epc", "elektrische_keuring"] : preset === "epc" ? ["epc"] : preset === "elektrisch" ? ["elektrische_keuring"] : [];
    onChange({ ...service, addOns });
  }

  return (
    <div>
      <h2>Welke keuring(en) wenst u?</h2>
      <p className="text-small text-muted">Meerdere opties mogelijk — kies het pakket dat het best bij uw situatie past.</p>

      <div className="grid grid-4" role="group" aria-label="Keuringen">
        {[
          { id: "attest" as Preset, title: "Alleen asbestattest", desc: "Het verplichte asbestattest voor de verkoop." },
          { id: "epc" as Preset, title: "+ EPC", desc: "Asbestattest samen met een energieprestatiecertificaat." },
          { id: "elektrisch" as Preset, title: "+ Elektrische keuring", desc: "Asbestattest samen met een keuring van de elektrische installatie." },
          { id: "combo" as Preset, title: "+ EPC en elektrische keuring", desc: "Alle drie combineren aan een voordeliger combinatietarief." },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            className="option-card"
            aria-pressed={current === opt.id}
            onClick={() => selectPreset(opt.id)}
            style={{ minHeight: 120 }}
          >
            <span className="option-card__check">
              <CheckIcon size={12} />
            </span>
            <FlaskIcon size={22} />
            <strong>{opt.title}</strong>
            <span className="text-tiny text-muted">{opt.desc}</span>
          </button>
        ))}
      </div>

      <div className="card card-pad mt-6">
        <h3>Extra staalnames</h3>
        <p className="text-small text-muted">Twee staalnames zijn standaard inbegrepen. Vermoedt u meer asbesthoudende materialen? Voeg extra staalnames toe.</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-label="Minder extra staalnames"
            onClick={() => onChange({ ...service, extraSamples: Math.max(0, service.extraSamples - 1) })}
          >
            −
          </button>
          <span style={{ minWidth: 32, textAlign: "center", fontWeight: 700 }}>{service.extraSamples}</span>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-label="Meer extra staalnames"
            onClick={() => onChange({ ...service, extraSamples: Math.min(20, service.extraSamples + 1) })}
          >
            +
          </button>
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="checkbox-field mb-0">
          <input
            type="checkbox"
            id="urgent"
            checked={service.urgent}
            onChange={(e: any) => onChange({ ...service, urgent: e.target.checked })}
          />
          <label htmlFor="urgent">
            <strong>
              <ClockIcon size={14} /> Spoedafspraak
            </strong>
            <br />
            Sneller een afspraak en attest nodig? Tegen een toeslag plannen we u met voorrang in.
          </label>
        </div>
      </div>
    </div>
  );
}
